import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Download, Eye, Loader2, Calendar, ChevronDown, Filter } from 'lucide-react';

// --- MOCK API DATA ---
const mockPayslips = [
  { id: 101, period: 'Dec 2024', amount: '8,500.00', status: 'Paid', date: '2024-12-30', file: 'payslip_dec_24.pdf' },
  { id: 102, period: 'Nov 2024', amount: '7,900.00', status: 'Paid', date: '2024-11-30', file: 'payslip_nov_24.pdf' },
  { id: 103, period: 'Oct 2024', amount: '8,100.00', status: 'Paid', date: '2024-10-30', file: 'payslip_oct_24.pdf' },
  { id: 104, period: 'Sep 2024', amount: '8,200.00', status: 'Paid', date: '2024-09-30', file: 'payslip_sep_24.pdf' },
  { id: 105, period: 'Aug 2024', amount: '8,000.00', status: 'Paid', date: '2024-08-30', file: 'payslip_aug_24.pdf' },
];

// --- API MOCK FUNCTION ---
const fetchPayslipData = async () => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ data: mockPayslips });
    }, 1000); // Simulate network delay
  });
};

// --- FRAMER MOTION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

// --- UTILITY COMPONENT: PayslipRow ---
const PayslipRow = ({ payslip }) => {
  const handleAction = (action, file) => {
    console.log(`${action} file: ${file}`);
    alert(`${action}ing ${payslip.period} Payslip!`);
  };

  return (
    <motion.tr 
      variants={itemVariants} 
      className="border-b border-indigo-100 hover:bg-indigo-50 transition-colors duration-200"
    >
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payslip.period}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">${payslip.amount}</td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
          {payslip.status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">{payslip.date}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-3">
          <motion.button 
            whileHover={{ scale: 1.1 }} 
            whileTap={{ scale: 0.95 }} 
            onClick={() => handleAction('View', payslip.file)}
            className="text-indigo-600 hover:text-indigo-900 transition-colors p-1 rounded-full bg-indigo-100/50"
            aria-label={`View payslip for ${payslip.period}`}
          >
            <Eye size={18} />
          </motion.button>
          <motion.button 
            whileHover={{ scale: 1.1 }} 
            whileTap={{ scale: 0.95 }} 
            onClick={() => handleAction('Download', payslip.file)}
            className="text-green-600 hover:text-green-900 transition-colors p-1 rounded-full bg-green-100/50"
            aria-label={`Download payslip for ${payslip.period}`}
          >
            <Download size={18} />
          </motion.button>
        </div>
      </td>
    </motion.tr>
  );
};

// --- MAIN COMPONENT: PayslipHistory ---
const PayslipHistory = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState('2024');

  // Fetch data on component mount
  useEffect(() => {
    setLoading(true);
    // In a real app, you would use axios.get('/api/payslips?year=' + filterYear)
    fetchPayslipData() 
      .then(response => {
        // Simple client-side filtering for the mock data
        const filteredData = response.data.filter(p => p.date.includes(filterYear));
        setPayslips(filteredData);
        setLoading(false);
      })
      .catch(error => {
        console.error("Failed to fetch payslips:", error);
        setLoading(false);
        // Handle error display here
      });
  }, [filterYear]); // Re-fetch when filterYear changes

  // Filter options for the dropdown
  const availableYears = ['2024', '2023', '2022'];

  return (
    <div className="min-h-screen p-4 sm:p-8 md:p-12 bg-gray-50 dark:bg-gray-900 transition-colors duration-500">
      
      {/* Cool Animated Header */}
      <motion.header
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 120 }}
        className="mb-8 md:mb-12 border-b-4 border-indigo-500 pb-4"
      >
        <h1 className="text-4xl font-extrabold text-indigo-700 dark:text-indigo-400 tracking-tight">
          Payslip History
        </h1>
        <p className="mt-1 text-lg text-gray-500 dark:text-gray-400">
          Access your secure and complete payroll records.
        </p>
      </motion.header>

      {/* Control Panel: Filters */}
      <motion.div
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center space-x-4 mb-6"
      >
        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 p-2 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <Filter size={20} className="text-indigo-500" />
          <label htmlFor="year-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">Filter by Year:</label>
          <div className="relative">
            <select
              id="year-filter"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="appearance-none bg-transparent py-1 pl-3 pr-8 text-sm font-medium text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-indigo-500" />
          </div>
        </div>
      </motion.div>


      {/* Main Content Area */}
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden ring-1 ring-black ring-opacity-5"
      >
        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 text-indigo-600 dark:text-indigo-400">
            <motion.div 
              animate={{ rotate: 360 }} 
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Loader2 size={36} />
            </motion.div>
            <p className="mt-4 text-lg font-medium">Loading Payslips...</p>
          </div>
        ) : payslips.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <Calendar size={48} className="mx-auto mb-4" />
            <p className="text-xl font-semibold">No Payslips found for {filterYear}.</p>
          </div>
        ) : (
          /* Data Table */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-indigo-200">
              <thead className="bg-indigo-50 dark:bg-indigo-900/50">
                <tr>
                  {['Period', 'Net Amount (USD)', 'Status', 'Date Paid', 'Actions'].map((header, index) => (
                    <th
                      key={header}
                      scope="col"
                      className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 ${header === 'Date Paid' ? 'hidden sm:table-cell' : ''}`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <motion.tbody
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="divide-y divide-indigo-100 bg-white dark:bg-gray-800 dark:divide-gray-700"
              >
                {payslips.map(payslip => (
                  <PayslipRow key={payslip.id} payslip={payslip} />
                ))}
              </motion.tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PayslipHistory;